import { useRef, useState, type ReactNode } from 'react';
import { Camera, Image as ImageIcon, Trash2 } from 'lucide-react';
import type { RumView } from '@cercle/shared';
import { useApi } from '../app-context';
import { errorMessage } from '../http/http-client';
import { preparePhoto } from '../photo/prepare-photo';
import { Button, ErrorText, Muted } from '../ui';
import styles from './rum-photo.module.scss';

/**
 * La photo d'une bouteille.
 *
 * Deux entrées distinctes plutôt qu'une. `capture="environment"` ouvre directement
 * l'appareil arrière du téléphone : c'est le geste attendu en cave, une main sur la
 * bouteille. Le second bouton, sans `capture`, ouvre la photothèque — pour la bouteille
 * qu'on a déjà prise en photo la semaine dernière, ou pour un ordinateur qui n'a pas
 * d'appareil. Un seul champ aurait forcé l'un des deux usages.
 *
 * L'envoi part dès la sélection. Demander une validation supplémentaire après avoir
 * déclenché l'appareil ajouterait un geste à un moment où l'on a les mains prises.
 *
 * Les métadonnées de la photo — dont les coordonnées GPS du lieu de la prise de vue —
 * sont retirées côté serveur, avant tout enregistrement. Le fichier qui quitte le
 * téléphone les porte encore ; celui qui atterrit dans la cave, non.
 */
export interface RumPhotoFieldProps {
  /**
   * `null` sur la page de création : la fiche n'existe pas encore, il n'y a rien à quoi
   * attacher la photo. Le fichier est alors gardé de côté et remis à l'appelant, qui
   * l'enverra une fois la fiche créée.
   */
  rumId: string | null;
  photoUrl: string | null;
  onChange?: (rum: RumView) => void;
  onPicked?: (file: File | null) => void;
  /**
   * `false` : la photo s'affiche, mais sans les boutons pour la changer.
   *
   * Tout le monde regarde la bouteille ; seuls son auteur et la modération la reprennent
   * (`canEditRum`). Proposer les boutons à un membre que l'API refusera est précisément ce
   * que la table des capacités s'interdit — « un bouton qui mène à un 403 est une promesse
   * rompue ». C'était le cas ici jusqu'à présent.
   */
  editable?: boolean;
}

export function RumPhotoField({
  rumId,
  photoUrl,
  onChange,
  onPicked,
  editable = true,
}: RumPhotoFieldProps): ReactNode {
  const api = useApi();
  const [pending, setPending] = useState<string | null>(null);
  // Remplacer une photo supprime l'ancien fichier ; une balise qui portait encore son
  // URL la redemande et reçoit un 404. On montre la silhouette plutôt qu'une image cassée.
  const [broken, setBroken] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(original: File | undefined): Promise<void> {
    if (!original) return;

    // Conversion avant tout le reste : un iPhone envoie du HEIC de quatre mégaoctets, que
    // l'API refuse deux fois — sur le format et sur le poids. Le navigateur sait le décoder
    // puisque le codec est dans l'appareil ; on le réencode en JPEG réduit.
    setBusy(true);
    let file: File;
    try {
      file = await preparePhoto(original);
    } finally {
      setBusy(false);
    }

    // Fiche pas encore créée : on montre un aperçu local et on garde le fichier.
    if (!rumId) {
      setPending((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(file);
      });
      setBroken(false);
      onPicked?.(file);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      setBroken(false);
      onChange?.(await api.rums.setPhoto(rumId, file));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
      // Sans cela, reprendre deux fois la même photo n'émettrait pas de second `change`.
      if (cameraRef.current) cameraRef.current.value = '';
      if (libraryRef.current) libraryRef.current.value = '';
    }
  }

  async function remove(): Promise<void> {
    if (!rumId) {
      setPending((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      onPicked?.(null);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      onChange?.(await api.rums.removePhoto(rumId));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const shown = pending ?? photoUrl;

  return (
    <div className={styles.field}>
      {shown && !broken ? (
        <img
          className={styles.preview}
          src={shown}
          alt="La bouteille"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          <Camera size={26} strokeWidth={1.5} />
        </div>
      )}

      {editable ? (
        <>
          {/* Les champs restent hors flux : ce sont les boutons qui les déclenchent. */}
          <input
            ref={cameraRef}
            className={styles.hidden}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(event) => void upload(event.target.files?.[0])}
          />
          <input
            ref={libraryRef}
            className={styles.hidden}
            type="file"
            accept="image/*"
            onChange={(event) => void upload(event.target.files?.[0])}
          />

          <div className={styles.actions}>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => cameraRef.current?.click()}
            >
              <Camera size={17} strokeWidth={1.5} aria-hidden="true" />
              {busy ? 'Envoi…' : shown ? 'Reprendre' : 'Photographier'}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => libraryRef.current?.click()}>
              <ImageIcon size={17} strokeWidth={1.5} aria-hidden="true" />
              Choisir un fichier
            </Button>
            {shown ? (
              <Button variant="ghost" disabled={busy} onClick={() => void remove()}>
                <Trash2 size={17} strokeWidth={1.5} aria-hidden="true" />
                Retirer
              </Button>
            ) : null}
          </div>

          <ErrorText>{error}</ErrorText>
          <Muted>
            La photo est réduite avant l’envoi, et le lieu de la prise de vue en est retiré
            avant son enregistrement.
          </Muted>
        </>
      ) : null}
    </div>
  );
}
